import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  CalendarDays, ChevronLeft, ChevronRight, Plus, Phone, MapPin, Mail, MessageSquare, Check, Loader2, AlertCircle, Paperclip,
} from 'lucide-react';
import ActivityModal from '../components/sales/ActivityModal';
import { OUTCOMES, labelFor, colorFor, apiError, AttachmentLinks } from '../components/sales/salesShared';
import { FilesEditor, uploadFiles } from '../components/sales/FilesEditor';
import * as P from '../lib/planner';

// Sales Planner: every rep's meetings and calls on an hour-by-hour grid, like
// a paper day planner. Everyone sees everyone; "Show" narrows it to yourself
// or one rep, notes and proposals included. Only the rep who owns an activity,
// admins and sales admins can change it (can_edit comes from the server).

const PX_PER_HOUR = 56;
const GUTTER = 56;

const KIND_ICONS = { visit: MapPin, call: Phone, email: Mail, other: MessageSquare };
const INCLUDE_CHIPS = [
  { k: 'visit', l: 'In-person meetings' },
  { k: 'call', l: 'Phone calls' },
  { k: 'other', l: 'Other contact' },
  { k: 'planned', l: 'Planned' },
  { k: 'email', l: 'Emails' },
];
const VIEWS = [{ k: 'day', l: 'Day' }, { k: 'week', l: 'Week' }, { k: 'month', l: 'Month' }];
const WEEKDAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const initials = (name) => (name || '?').split(/\s+/).filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase();

const statusOf = (a) => {
  if (a.state === 'planned') return { label: 'Planned', color: '#CBD5E1' };
  if (a.state === 'missed') return { label: 'Not logged yet', color: '#FBBF24' };
  if (a.outcome) return { label: labelFor(OUTCOMES, a.outcome), color: colorFor(OUTCOMES, a.outcome) };
  return { label: 'Done', color: '#CBD5E1' };
};

const blockColors = (a, color) => {
  if (a.state === 'missed') return { background: 'rgba(245,158,11,0.10)', border: '1.5px dashed #F59E0B' };
  if (a.state === 'planned') return { background: 'rgba(18,18,18,0.75)', border: `1.5px dashed ${color}` };
  return { background: `${color}29`, border: `1px solid ${color}` };
};

const timeRange = (a) => (a.timed ? `${P.fmtTime(a.startMin)} – ${P.fmtTime(a.startMin + a.length)}` : 'No time set');

// The badge on a meeting that has files attached.
const FilesBadge = ({ id }) => (
  <span className="ml-auto shrink-0 rounded bg-[#A855F7] text-white p-0.5" title="Files attached" data-testid={`planner-files-${id}`}>
    <Paperclip className="w-2.5 h-2.5" aria-hidden="true" />
  </span>
);

// One activity on the grid.
const Block = ({ a, color, startHour, selected, onPick, prefix, repId }) => {
  const box = P.blockBox(a, startHour, PX_PER_HOUR);
  const Icon = KIND_ICONS[a.kind] || MessageSquare;
  const st = statusOf(a);
  const lanes = a.lanes || 1;
  const lane = a.lane || 0;
  const other = P.otherRepName(a, repId);
  return (
    <button
      type="button"
      onClick={() => onPick(a.id)}
      aria-label={`${P.KIND_LABELS[a.kind]}, ${a.advertiser_name || 'client'}, ${timeRange(a)}, ${st.label}${other ? `, with ${other}` : ''}${a.has_files ? ', files attached' : ''}`}
      data-testid={`planner-block-${a.id}`}
      className="absolute rounded-md px-2 py-1 text-left overflow-hidden flex flex-col gap-0.5 z-[1]"
      style={{
        top: box.top + 1,
        height: box.height - 2,
        left: `calc(${(lane * 100) / lanes}% + 3px)`,
        width: `calc(${100 / lanes}% - 6px)`,
        ...blockColors(a, color),
        boxShadow: selected ? '0 0 0 2px #F8FAFC' : 'none',
      }}
    >
      <span className="flex items-center gap-1.5 text-xs font-semibold text-[#F8FAFC] truncate w-full">
        <Icon className="w-3 h-3 shrink-0" aria-hidden="true" />
        {prefix && lanes === 1 && <span className="text-[#CBD5E1]">{prefix}</span>}
        <span className="truncate">{a.advertiser_name || 'Client'}</span>
        {a.has_files && <FilesBadge id={a.id} />}
      </span>
      {box.height >= 44 && (
        <>
          <span className="text-[11px] text-[#CBD5E1] truncate w-full">{timeRange(a)}</span>
          <span className="text-[11px] font-semibold truncate w-full" style={{ color: st.color }}>
            {st.label}{other && <span className="text-[#CBD5E1] font-normal" data-testid={`planner-with-${a.id}`}> · with {other}</span>}
          </span>
        </>
      )}
    </button>
  );
};

// A small chip for an activity that has a day but no time of day.
const UntimedChip = ({ a, color, selected, onPick, prefix }) => {
  const Icon = KIND_ICONS[a.kind] || MessageSquare;
  return (
    <button
      type="button"
      onClick={() => onPick(a.id)}
      data-testid={`planner-untimed-${a.id}`}
      className="w-full flex items-center gap-1.5 text-[11px] text-[#F8FAFC] rounded px-1.5 py-1 truncate"
      style={{ ...blockColors(a, color), boxShadow: selected ? '0 0 0 2px #F8FAFC' : 'none' }}
    >
      <Icon className="w-3 h-3 shrink-0" aria-hidden="true" />
      {prefix && <span className="text-[#CBD5E1]">{prefix}</span>}
      <span className="truncate">{a.advertiser_name || 'Client'}</span>
      {a.has_files && <FilesBadge id={a.id} />}
    </button>
  );
};

// An hour grid: one column per entry of `columns` ({ key, title, sub, items, color(a), prefix(a), highlight, repId }).
const HourGrid = ({ columns, hours, selectedId, onPick, nowMin, showNowIn, colMin = 150 }) => {
  const height = (hours.end - hours.start) * PX_PER_HOUR;
  const hourList = [];
  for (let h = hours.start; h < hours.end; h += 1) hourList.push(h);
  const anyUntimed = columns.some(c => c.items.some(a => !a.timed));
  const colTemplate = `${GUTTER}px repeat(${columns.length}, minmax(${colMin}px, 1fr))`;
  const nowTop = nowMin != null ? ((nowMin - hours.start * 60) / 60) * PX_PER_HOUR : null;
  const nowVisible = nowTop != null && nowTop >= 0 && nowTop <= height;
  return (
    <div className="overflow-x-auto" data-testid="planner-grid">
      <div className="grid" style={{ gridTemplateColumns: colTemplate, minWidth: GUTTER + columns.length * colMin }}>
        <div className="border-b border-[#1E293B]" />
        {columns.map(c => (
          <div key={`h-${c.key}`} className={`border-b border-l border-[#1E293B] px-3 py-2 min-h-[60px] ${c.highlight ? 'bg-[#3B82F6]/10' : ''}`}>
            <div className="flex items-center gap-2">
              {c.dot && <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: c.dot }} aria-hidden="true" />}
              <span className="text-sm font-semibold text-[#F8FAFC] truncate">{c.title}</span>
            </div>
            {c.sub && <div className="text-xs text-[#94A3B8] mt-0.5">{c.sub}</div>}
            {c.warn && <div className="text-[11px] font-semibold text-amber-400 mt-0.5">{c.warn}</div>}
          </div>
        ))}

        {anyUntimed && (
          <>
            <div className="border-b border-[#1E293B] text-[11px] text-[#94A3B8] text-right pr-2 pt-2">No time</div>
            {columns.map(c => (
              <div key={`u-${c.key}`} className="border-b border-l border-[#1E293B] p-1.5 space-y-1">
                {c.items.filter(a => !a.timed).map(a => (
                  <UntimedChip key={a.id} a={a} color={c.color(a)} prefix={c.prefix && c.prefix(a)} selected={selectedId === a.id} onPick={onPick} />
                ))}
              </div>
            ))}
          </>
        )}

        <div className="relative" style={{ height }}>
          {hourList.map(h => (
            <span key={h} className="absolute right-2 text-[11px] text-[#94A3B8]" style={{ top: (h - hours.start) * PX_PER_HOUR + 2 }}>{P.fmtHour(h)}</span>
          ))}
        </div>
        {columns.map(c => {
          const timed = P.layoutLanes(c.items.filter(a => a.timed));
          return (
            <div
              key={`c-${c.key}`}
              className="relative border-l border-[#1E293B]"
              style={{
                height,
                backgroundImage: `repeating-linear-gradient(to bottom, #1E293B 0px, #1E293B 1px, transparent 1px, transparent ${PX_PER_HOUR}px)`,
              }}
            >
              {nowVisible && showNowIn(c) && (
                <div className="absolute left-0 right-0 h-0.5 bg-[#F87171] z-[2]" style={{ top: nowTop }} aria-hidden="true" />
              )}
              {timed.map(a => (
                <Block key={a.id} a={a} color={c.color(a)} prefix={c.prefix && c.prefix(a)} repId={c.repId} startHour={hours.start} selected={selectedId === a.id} onPick={onPick} />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const PlannerPage = () => {
  const { user } = useAuth();
  const prefsKey = `publishos.planner.${user?.id || user?._id || user?.email || 'me'}`;

  const [prefs, setPrefs] = useState(() => {
    let raw = null;
    try { raw = window.localStorage.getItem(prefsKey); } catch (e) { raw = null; }
    return P.readPrefs(raw);
  });
  useEffect(() => {
    try { window.localStorage.setItem(prefsKey, JSON.stringify(prefs)); } catch (e) { /* private window */ }
  }, [prefsKey, prefs]);
  const { view, show, include } = prefs;
  const setView = (v) => setPrefs(p => ({ ...p, view: v }));
  const setShow = (v) => setPrefs(p => ({ ...p, show: v }));
  const toggleInclude = (k) => setPrefs(p => ({ ...p, include: { ...p.include, [k]: !p.include[k] } }));

  const [date, setDate] = useState(() => P.startOfDay(new Date()));
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [modal, setModal] = useState(null);   // { planned, activity, startAt }
  const [busy, setBusy] = useState(false);

  const range = useMemo(() => P.rangeFor(view, date), [view, date]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const q = `date_from=${encodeURIComponent(range.from.toISOString())}&date_to=${encodeURIComponent(range.to.toISOString())}`;
      setData(await api.get(`/sales/planner?${q}`));
    } catch (e) {
      setError(apiError(e, 'Could not load the planner'));
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const items = useMemo(() => P.prepare(data?.activities, now), [data, now]);
  const myRepId = data?.my_rep_id || null;
  const allReps = useMemo(() => P.repsFor(data?.reps, items, 'all', myRepId), [data, items, myRepId]);
  const reps = useMemo(() => P.repsFor(data?.reps, items, show, myRepId), [data, items, show, myRepId]);
  const repById = useMemo(() => Object.fromEntries(allReps.map(r => [r.id, r])), [allReps]);
  const shownIds = useMemo(() => new Set(reps.map(r => r.id)), [reps]);
  const shownItems = useMemo(() => items.filter(a => shownIds.has(a.sales_rep_id) || shownIds.has(a.accompanied_by_rep_id)), [items, shownIds]);
  const visible = useMemo(() => shownItems.filter(a => P.isVisible(a, include)), [shownItems, include]);
  const colorOf = (a) => repById[a.sales_rep_id]?.color || '#94A3B8';

  const weekStart = P.startOfWeek(date);
  const weekEnd = P.addDays(weekStart, 7);
  const today = P.startOfDay(now);
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const selected = items.find(a => a.id === selectedId) || null;

  const canEdit = (a) => !!a.can_edit;

  const cancelPlanned = async (a) => {
    setBusy(true);
    try {
      await api.put(`/sales/activities/${a.id}`, { status: 'cancelled' });
      setSelectedId(null);
      await load();
    } catch (e) {
      setError(apiError(e, 'Could not cancel it'));
    } finally {
      setBusy(false);
    }
  };

  // The files on a meeting. The whole list is saved each time.
  const saveFiles = async (a, attachments) => {
    await api.put(`/sales/activities/${a.id}`, { attachments });
    await load();
  };

  const addFiles = async (chosen, onProgress) => {
    const a = selected;
    if (!a) return;
    setBusy(true);
    setError('');
    try {
      const { uploaded, error: problem } = await uploadFiles(chosen, a.attachments.length, onProgress);
      if (uploaded.length) await saveFiles(a, [...a.attachments, ...uploaded]);
      if (problem) setError(problem);
    } catch (e) {
      setError(apiError(e, 'Could not save the files'));
    } finally {
      setBusy(false);
    }
  };

  const removeFile = async (index) => {
    const a = selected;
    if (!a) return;
    setBusy(true);
    setError('');
    try {
      await saveFiles(a, a.attachments.filter((_, n) => n !== index));
    } catch (e) {
      setError(apiError(e, 'Could not remove the file'));
    } finally {
      setBusy(false);
    }
  };

  const scheduleNew = () => {
    let startAt = null;
    if (view === 'day' && date > today) {
      startAt = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 9, 0);
    }
    setModal({ planned: true, activity: null, startAt });
  };

  const showOptions = [
    { v: 'all', l: 'Everyone' },
    { v: 'me', l: 'Only my activity' },
    ...allReps.map(r => ({ v: r.id, l: r.name })),
  ];

  // --- Week summary cards (Day and Week views) --------------------------------
  const summary = reps.map(r => {
    const s = P.repStats(items, r.id, weekStart, weekEnd);
    return { ...r, s, pace: P.pace(s.meet, r.target, weekStart, now) };
  });

  // --- Day view ------------------------------------------------------------
  const renderDay = () => {
    const key = P.dayKey(date);
    const dayItems = visible.filter(a => a.day === key);
    const hours = P.hourRange(dayItems.filter(a => a.timed));
    const columns = reps.map(r => {
      const mine = items.filter(a => P.onRep(a, r.id) && a.day === key);
      const st = P.repStats(mine, r.id, date, P.addDays(date, 1));
      const hiddenEmails = include.email ? 0 : st.emails;
      return {
        key: r.id,
        title: r.name,
        dot: r.color,
        repId: r.id,
        sub: `${st.meet} meeting${st.meet === 1 ? '' : 's'} · ${st.calls} call${st.calls === 1 ? '' : 's'}${hiddenEmails ? ` · ${hiddenEmails} email${hiddenEmails === 1 ? '' : 's'} hidden` : ''}`,
        items: dayItems.filter(a => P.onRep(a, r.id)),
        color: () => r.color,
      };
    });
    return (
      <HourGrid
        columns={columns}
        hours={hours}
        selectedId={selectedId}
        onPick={setSelectedId}
        nowMin={P.sameDay(date, now) ? nowMin : null}
        showNowIn={() => true}
      />
    );
  };

  // --- Week view -----------------------------------------------------------
  const renderWeek = () => {
    const dates = [0, 1, 2, 3, 4, 5, 6].map(i => P.addDays(weekStart, i));
    const days = P.visibleDays(dates, P.weekendShown(visible, dates));
    const weekItems = visible.filter(a => a.start >= weekStart && a.start < weekEnd);
    const hours = P.hourRange(weekItems.filter(a => a.timed));
    const many = reps.length > 1;
    const columns = days.map(d => {
      const key = P.dayKey(d);
      const dayAll = shownItems.filter(a => a.day === key);
      const meet = dayAll.filter(a => a.state === 'done' && a.kind === 'visit').length;
      const emails = dayAll.filter(a => a.state === 'done' && a.kind === 'email').length;
      const past = d <= today && !P.isWeekend(d);
      return {
        key,
        title: `${d.toLocaleDateString(undefined, { weekday: 'short' })} ${d.getDate()}`,
        sub: `${meet} meeting${meet === 1 ? '' : 's'}${emails && !include.email ? ` · ${emails} email${emails === 1 ? '' : 's'} hidden` : ''}`,
        warn: past && meet === 0 && reps.length ? 'No in-person meetings' : '',
        highlight: P.sameDay(d, now),
        items: weekItems.filter(a => a.day === key),
        color: colorOf,
        prefix: many ? (a) => `${initials(repById[a.sales_rep_id]?.name || a.sales_rep_name)}${a.accompanied_by_rep_id ? ` + ${initials(a.accompanied_by_name)}` : ''}` : null,
        date: d,
      };
    });
    return (
      <HourGrid
        columns={columns}
        hours={hours}
        selectedId={selectedId}
        onPick={setSelectedId}
        nowMin={nowMin}
        showNowIn={(c) => P.sameDay(c.date, now)}
        colMin={110}
      />
    );
  };

  // --- Month view ----------------------------------------------------------
  const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
  const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 1);

  const renderMonth = () => {
    const weeks = P.monthWeeks(date);
    const allDates = weeks.flat();
    const shown = P.weekendShown(shownItems, allDates);
    const dayCols = [0, 1, 2, 3, 4, 5, 6].filter(i => i < 5 || shown[i === 5 ? 6 : 0]);
    const counts = {};
    shownItems.forEach(a => {
      [a.sales_rep_id, a.accompanied_by_rep_id].filter(Boolean).forEach(repId => {
        const k = `${repId}|${a.day}`;
        const c = counts[k] || (counts[k] = { meet: 0, calls: 0, planned: 0 });
        if (a.state !== 'done') c.planned += 1;
        else if (a.kind === 'visit') c.meet += 1;
        else if (a.kind === 'call') c.calls += 1;
      });
    });
    const rows = reps.map(r => {
      const s = P.repStats(items, r.id, monthStart, monthEnd);
      let workdays = 0;
      let idle = 0;
      for (let d = monthStart; d < monthEnd && d <= today; d = P.addDays(d, 1)) {
        if (P.isWeekend(d)) continue;
        workdays += 1;
        if (!(counts[`${r.id}|${P.dayKey(d)}`]?.meet)) idle += 1;
      }
      return { ...r, s, workdays, idle };
    });
    return (
      <div className="space-y-4">
        <div className="overflow-x-auto" data-testid="planner-month">
          <div className="grid" style={{ gridTemplateColumns: `repeat(${dayCols.length}, minmax(130px, 1fr))`, minWidth: dayCols.length * 130 }}>
            {dayCols.map(i => (
              <div key={`d-${i}`} className="px-2 py-2 text-xs font-semibold text-[#94A3B8] border-b border-[#1E293B]">{WEEKDAY_NAMES[i]}</div>
            ))}
            {weeks.map(week => dayCols.map(i => {
              const d = week[i];
              const inMonth = d.getMonth() === date.getMonth();
              const key = P.dayKey(d);
              const isToday = P.sameDay(d, now);
              const future = d > today;
              return (
                <button
                  type="button"
                  key={key}
                  onClick={() => { setDate(d); setView('day'); }}
                  className={`min-h-[112px] p-2 text-left border-b border-r border-[#1E293B] flex flex-col gap-1 hover:bg-[#1E293B]/40 ${inMonth ? '' : 'opacity-50'} ${isToday ? 'bg-[#3B82F6]/10' : ''}`}
                  data-testid={`planner-month-day-${key}`}
                  aria-label={`Open ${d.toDateString()}`}
                >
                  <span className={`text-xs font-semibold ${isToday ? 'text-[#60A5FA]' : 'text-[#F8FAFC]'}`}>{d.getDate()}</span>
                  {reps.map(r => {
                    const c = counts[`${r.id}|${key}`] || { meet: 0, calls: 0, planned: 0 };
                    if (future && !c.planned) return null;
                    const warnZero = !future && !P.isWeekend(d) && inMonth && c.meet === 0 && r.target > 0;
                    return (
                      <span key={r.id} className="flex items-center gap-1.5 text-xs">
                        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: r.color }} aria-hidden="true" />
                        <span className="w-6 text-[#94A3B8]">{initials(r.name)}</span>
                        {future ? (
                          <span className="text-[#CBD5E1]">{c.planned} planned</span>
                        ) : (
                          <>
                            <span className={`font-bold ${warnZero ? 'text-amber-400' : 'text-[#F8FAFC]'}`}>{c.meet}</span>
                            <span className="text-[#94A3B8]">· {c.calls}</span>
                          </>
                        )}
                      </span>
                    );
                  })}
                </button>
              );
            }))}
          </div>
        </div>
        <p className="text-xs text-[#94A3B8]">Each line: <strong className="text-[#F8FAFC]">in-person meetings</strong> · calls. Amber 0 = no meeting that weekday (for reps with a target). Click a day to open it.</p>

        <div className="border border-[#1E293B] rounded-lg overflow-x-auto" data-testid="planner-month-totals">
          <table className="w-full text-sm min-w-[760px]">
            <thead>
              <tr className="text-left text-[#94A3B8] text-xs">
                <th className="px-3 py-2 font-medium">Rep</th>
                <th className="px-3 py-2 font-medium">In-person meetings</th>
                <th className="px-3 py-2 font-medium">Phone calls</th>
                <th className="px-3 py-2 font-medium">Other contact</th>
                <th className="px-3 py-2 font-medium">Emails</th>
                <th className="px-3 py-2 font-medium">Sales won</th>
                <th className="px-3 py-2 font-medium">Planned, not logged</th>
                <th className="px-3 py-2 font-medium">Weekdays with no meeting</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-t border-[#1E293B]">
                  <td className="px-3 py-2 text-[#F8FAFC]">
                    <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: r.color }} aria-hidden="true" />{r.name}</span>
                  </td>
                  <td className="px-3 py-2 font-bold text-[#F8FAFC]">{r.s.meet}</td>
                  <td className="px-3 py-2 text-[#F8FAFC]">{r.s.calls}</td>
                  <td className="px-3 py-2 text-[#F8FAFC]">{r.s.other}</td>
                  <td className="px-3 py-2 text-[#94A3B8]">{r.s.emails}</td>
                  <td className="px-3 py-2 font-semibold text-emerald-400">{r.s.won}</td>
                  <td className={`px-3 py-2 ${r.s.missed ? 'text-amber-400' : 'text-[#F8FAFC]'}`}>{r.s.missed}</td>
                  <td className="px-3 py-2 text-[#F8FAFC]">{r.idle} of {r.workdays}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // --- Page ----------------------------------------------------------------
  const emptyMsg = !loading && data && (
    !allReps.length
      ? 'No sales reps yet. An admin can add them in Settings → Sales Reps.'
      : (!reps.length && show === 'me'
        ? `Your user is not linked to a sales rep record, so there is no activity of yours to show. Ask an admin to put your email (${user?.email || ''}) on your Sales Rep record in Settings.`
        : (!reps.length ? 'That rep is no longer on the list. Pick another in Show.' : ''))
  );

  const selStatus = selected ? statusOf(selected) : null;
  const selRep = selected ? repById[selected.sales_rep_id] : null;

  return (
    <div className="space-y-5" data-testid="planner-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-[#F8FAFC] tracking-tight flex items-center gap-3">
            <CalendarDays className="w-7 h-7 text-[#3B82F6]" /> Sales Planner
          </h1>
          <p className="text-sm text-[#94A3B8] mt-1">Who met with which client, and when.</p>
        </div>
        <Button onClick={scheduleNew} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="planner-schedule-btn">
          <Plus className="w-4 h-4 mr-2" /> Schedule meeting
        </Button>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 p-2 bg-[#121212] border border-[#1E293B] rounded-lg">
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" aria-label="Previous" onClick={() => setDate(d => P.shiftDate(view, d, -1))} className="border-[#1E293B] bg-transparent text-[#F8FAFC] h-9 w-9 p-0" data-testid="planner-prev">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setDate(P.startOfDay(new Date()))} className="border-[#1E293B] bg-transparent text-[#F8FAFC] h-9" data-testid="planner-today">Today</Button>
          <Button variant="outline" size="sm" aria-label="Next" onClick={() => setDate(d => P.shiftDate(view, d, 1))} className="border-[#1E293B] bg-transparent text-[#F8FAFC] h-9 w-9 p-0" data-testid="planner-next">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <span className="text-base font-semibold text-[#F8FAFC] px-2" data-testid="planner-range-label">{P.rangeLabel(view, date)}</span>
          {loading && <Loader2 className="w-4 h-4 animate-spin text-[#94A3B8]" aria-label="Loading" />}
        </div>
        <div className="flex border border-[#1E293B] rounded-md overflow-hidden" role="group" aria-label="View">
          {VIEWS.map(v => (
            <button
              type="button" key={v.k}
              aria-pressed={view === v.k}
              onClick={() => setView(v.k)}
              className={`h-9 px-3.5 text-sm ${view === v.k ? 'bg-[#1E293B] text-[#F8FAFC] font-medium' : 'text-[#94A3B8] hover:text-[#F8FAFC]'}`}
              data-testid={`planner-view-${v.k}`}
            >{v.l}</button>
          ))}
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <span className="text-sm text-[#94A3B8]">Show</span>
          <Select value={showOptions.some(o => o.v === show) ? show : 'all'} onValueChange={setShow}>
            <SelectTrigger className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] w-[210px] h-9" data-testid="planner-show">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-[#1E293B]">
              {showOptions.map(o => <SelectItem key={o.v} value={o.v} className="text-[#F8FAFC]">{o.l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Include filters */}
      <div className="flex flex-wrap items-center gap-2" data-testid="planner-include">
        <span className="text-sm text-[#94A3B8] mr-1">Include</span>
        {INCLUDE_CHIPS.map(c => (
          <button
            type="button" key={c.k}
            aria-pressed={!!include[c.k]}
            onClick={() => toggleInclude(c.k)}
            className={`h-8 px-3 rounded-full text-sm flex items-center gap-1.5 border ${include[c.k] ? 'border-[#3B82F6] bg-[#3B82F6]/15 text-[#F8FAFC]' : 'border-[#1E293B] text-[#94A3B8] hover:text-[#F8FAFC]'}`}
            data-testid={`planner-include-${c.k}`}
          >
            {include[c.k] && <Check className="w-3.5 h-3.5" aria-hidden="true" />}{c.l}
          </button>
        ))}
      </div>

      {error && (
        <div className="text-sm text-red-400 bg-red-900/20 border border-red-800 rounded-md p-2 flex items-center gap-2" data-testid="planner-error">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}
      {emptyMsg && (
        <div className="text-sm text-amber-200 bg-amber-900/20 border border-amber-700/50 rounded-md p-3" data-testid="planner-empty">{emptyMsg}</div>
      )}

      {/* Week so far (Day and Week views) */}
      {view !== 'month' && summary.length > 0 && (
        <section className="space-y-2" data-testid="planner-summary">
          <h2 className="text-sm font-semibold text-[#F8FAFC]">
            {P.sameDay(weekStart, P.startOfWeek(now)) ? 'This week so far' : 'That week'}
            <span className="text-[#94A3B8] font-normal"> · {P.rangeLabel('week', date)}</span>
          </h2>
          <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
            {summary.map(r => (
              <div key={r.id} className="bg-[#121212] border border-[#1E293B] rounded-lg p-3 space-y-2" data-testid={`planner-summary-${r.id}`}>
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: r.color }} aria-hidden="true" />
                  <span className="text-sm font-semibold text-[#F8FAFC] truncate">{r.name}</span>
                  {r.pace === 'behind' && <span className="ml-auto text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400">Behind pace</span>}
                  {r.pace === 'on' && <span className="ml-auto text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400">On pace</span>}
                  {r.pace === 'met' && <span className="ml-auto text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400">Target met</span>}
                </div>
                <div className="grid grid-cols-3 gap-1">
                  <div><div className="text-xl font-bold text-[#F8FAFC]">{r.s.meet}</div><div className="text-[11px] text-[#94A3B8]">In-person</div></div>
                  <div><div className="text-xl font-bold text-[#F8FAFC]">{r.s.calls}</div><div className="text-[11px] text-[#94A3B8]">Calls</div></div>
                  <div><div className="text-xl font-bold text-emerald-400">{r.s.won}</div><div className="text-[11px] text-[#94A3B8]">Sales won</div></div>
                </div>
                {r.target > 0 ? (
                  <div className="space-y-1">
                    <div className="h-1.5 rounded-full bg-[#1E293B] overflow-hidden">
                      <div className="h-1.5" style={{ width: `${Math.min(100, Math.round((r.s.meet / r.target) * 100))}%`, background: r.color }} />
                    </div>
                    <div className="text-[11px] text-[#94A3B8]">{r.s.meet} of {r.target} meetings{r.s.missed ? ` · ${r.s.missed} not logged` : ''} · {r.s.emails} email{r.s.emails === 1 ? '' : 's'} not counted</div>
                  </div>
                ) : (
                  <div className="text-[11px] text-[#94A3B8]">No target set{r.s.missed ? ` · ${r.s.missed} not logged` : ''} · {r.s.emails} email{r.s.emails === 1 ? '' : 's'} not counted</div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Calendar + details */}
      {reps.length > 0 && (
        <div className="flex flex-wrap gap-4 items-start">
          <section className="flex-[999_1_560px] min-w-0 bg-[#121212] border border-[#1E293B] rounded-lg overflow-hidden">
            {view === 'day' && renderDay()}
            {view === 'week' && renderWeek()}
            {view === 'month' && <div className="p-3">{renderMonth()}</div>}
          </section>

          {view !== 'month' && (
            <aside className="flex-[1_1_280px] min-w-0 bg-[#121212] border border-[#1E293B] rounded-lg p-4 space-y-3" data-testid="planner-detail">
              {!selected ? (
                <p className="text-sm text-[#94A3B8]">Click a meeting or call to see what happened.</p>
              ) : (
                <>
                  <div className="space-y-1">
                    <div className="text-xs text-[#94A3B8]">{P.KIND_LABELS[selected.kind]}</div>
                    <Link to={`/advertisers/${selected.advertiser_id}`} className="text-lg font-bold text-[#F8FAFC] hover:text-[#60A5FA] block">
                      {selected.advertiser_name || 'Client'}
                    </Link>
                    <div className="flex items-center gap-2 text-sm text-[#CBD5E1]">
                      <span className="w-2 h-2 rounded-full" style={{ background: selRep?.color || '#94A3B8' }} aria-hidden="true" />
                      {selRep?.name || selected.sales_rep_name || 'Unknown rep'}
                    </div>
                  </div>
                  <dl className="grid grid-cols-[80px_1fr] gap-x-3 gap-y-1.5 text-sm">
                    <dt className="text-[#94A3B8]">When</dt>
                    <dd className="text-[#F8FAFC]">{selected.start.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })} · {timeRange(selected)}</dd>
                    {selected.timed && (<><dt className="text-[#94A3B8]">Length</dt><dd className="text-[#F8FAFC]">{P.fmtLength(selected.length)}</dd></>)}
                    {selected.accompanied_by_name && (<><dt className="text-[#94A3B8]">With</dt><dd className="text-[#F8FAFC]" data-testid="planner-detail-with">{selected.accompanied_by_name}</dd></>)}
                    <dt className="text-[#94A3B8]">Status</dt>
                    <dd className="font-semibold" style={{ color: selStatus.color }}>{selStatus.label}</dd>
                  </dl>
                  {selected.summary && (
                    <div>
                      <div className="text-xs text-[#94A3B8]">Summary</div>
                      <p className="text-sm text-[#F8FAFC] mt-0.5">{selected.summary}</p>
                    </div>
                  )}
                  {selected.notes && (
                    <div>
                      <div className="text-xs text-[#94A3B8]">Notes</div>
                      <p className="text-sm text-[#F8FAFC] mt-0.5 whitespace-pre-wrap">{selected.notes}</p>
                    </div>
                  )}
                  {(selected.has_files || canEdit(selected)) && (
                    <div data-testid="planner-detail-files">
                      <div className="text-xs text-[#94A3B8] mb-1">Files</div>
                      {canEdit(selected)
                        ? <FilesEditor files={selected.attachments || []} onAdd={addFiles} onRemove={removeFile} busy={busy} compact />
                        : <AttachmentLinks activity={selected} className="mt-1" />}
                    </div>
                  )}
                  {selected.state !== 'done' && (
                    <div className={`text-sm rounded-md p-2.5 ${selected.state === 'missed' ? 'bg-amber-500/10 text-amber-300' : 'bg-[#0A0A0A] text-[#CBD5E1]'}`}>
                      {selected.state === 'missed'
                        ? `This was planned for ${P.fmtTime(selected.startMin)} and nothing has been logged yet.`
                        : 'Booked ahead. After the meeting, log the result and the block turns solid.'}
                    </div>
                  )}
                  {selected.state !== 'done' && canEdit(selected) && (
                    <div className="flex flex-wrap gap-2">
                      <Button onClick={() => setModal({ planned: false, activity: selected, startAt: null })} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="planner-log-result">
                        Log the result
                      </Button>
                      <Button variant="outline" disabled={busy} onClick={() => cancelPlanned(selected)} className="border-[#1E293B] bg-transparent text-[#94A3B8]" data-testid="planner-cancel-planned">
                        Mark as cancelled
                      </Button>
                    </div>
                  )}
                </>
              )}
            </aside>
          )}
        </div>
      )}

      {view !== 'month' && reps.length > 0 && (
        <div className="flex flex-wrap gap-4 text-xs text-[#94A3B8] items-center">
          <span className="flex items-center gap-1.5"><span className="w-5 h-3.5 rounded bg-[#94A3B8]/20 border border-[#94A3B8]" aria-hidden="true" />Done (logged)</span>
          <span className="flex items-center gap-1.5"><span className="w-5 h-3.5 rounded border-[1.5px] border-dashed border-[#94A3B8]" aria-hidden="true" />Planned</span>
          <span className="flex items-center gap-1.5"><span className="w-5 h-3.5 rounded bg-amber-500/10 border-[1.5px] border-dashed border-amber-500" aria-hidden="true" />Planned, time passed, not logged</span>
          <span className="flex items-center gap-1.5"><span className="rounded bg-[#A855F7] text-white p-0.5" aria-hidden="true"><Paperclip className="w-2.5 h-2.5" /></span>Files attached</span>
          <span className="flex items-center gap-1.5"><span className="w-5 h-0.5 bg-[#F87171]" aria-hidden="true" />Now</span>
        </div>
      )}

      <ActivityModal
        open={!!modal}
        onClose={() => setModal(null)}
        planned={!!modal?.planned}
        startAt={modal?.startAt || null}
        activity={modal?.activity || null}
        onSaved={async () => { await load(); }}
      />
    </div>
  );
};

export default PlannerPage;
